import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    deepStringFind,
    errorRedirect,
    InfiniteScroll,
    inject,
    Loader,
    NothingHere,
    SubTopBar,
    Title,
    toArray,
    Tooltip,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsCardHeading } from '@react-icons/all-files/bs/BsCardHeading';
import { RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { Button, ButtonGroup, Input } from 'reactstrap';
import { AppStore } from '../Core/AppStore';
import { PartDefinitionItem } from './PartDefinitionItem';
import { PartDefinitionsStore } from './PartDefinitionsStore';

export class PartDefinitionGroup {
    constructor(model: Mri.Parts.PartDefinitionBase) {
        this.head = model;
    }

    @bind
    @action
    public toggle(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.open = !this.open;
    }

    @computed
    public get count() {
        if (this.head.partDefinitions) return this.head.partDefinitions.length;
        else return 0;
    }

    @computed
    public get canOpen() {
        return this.head.partDefinitions != undefined;
    }

    @computed
    public get topPartDef() {
        return this.head.partDefinitions[0];
    }

    @observable
    public head: Mri.Parts.PartDefinitionBase;
    @observable
    public open = false;
}

export interface IPartDefinitionListProps extends RouteComponentProps<undefined> {}

@autoinject
@observer
export class PartDefinitionsList extends React.Component<IPartDefinitionListProps> {
    @action
    public async componentDidMount() {
        this.loading = true;
        try {
            await this.partDefinitionsStore.loadAllAsync();
        } catch (e: any) {
            errorRedirect(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        return (
            <div className="container-fluid">
                <Title>Part Definitions</Title>

                <SubTopBar>
                    <LinkContainer to="/part-definitions/create">
                        <a className="btn btn-primary">Create Base</a>
                    </LinkContainer>

                    <div className="mx-2 border" />

                    <Input
                        type="text"
                        value={this.searchValue}
                        onChange={this.onSearchInputChange}
                        className="mw-450"
                        placeholder="Search Parts..."
                    />
                </SubTopBar>

                <div className="row">
                    <div className="col-12 px-0">
                        <InfiniteScroll
                            throttle={100}
                            threshold={300}
                            hasMore={this.hasMore}
                            onLoadMore={this.loadMore}
                            className="overflow-x-scroll"
                            container={this.appStore.mainContainer} // this.listContainer WHY THOUGH IT WON'T WORK
                        >
                            <table className="table table-striped w-100 h-100">
                                <thead>
                                    <tr>
                                        <th />
                                        <th>Part Number</th>

                                        <th>Revision</th>

                                        <th>Description</th>

                                        <th>Comments</th>

                                        <th>Top Level</th>

                                        <th>Part Count</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.groupedPartDefinitions.slice(0, this.scrollCount).map(g => (
                                        <PartDefinitionItem key={g.head.partNumber} part={g} />
                                    ))}
                                </tbody>
                            </table>

                            <NothingHere
                                empty={this.partDefinitionBases.length <= 0}
                                icon={<BsCardHeading />}
                                loading={this.loading}
                            />
                        </InfiniteScroll>
                    </div>
                </div>
            </div>
        );
    }

    @bind
    @action
    private async onSearchInputChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.searchValue = target.value;
    }

    @computed
    private get partDefinitionBases() {
        let partDefinitionBases = toArray(this.partDefinitionsStore.partDefinitionBases);

        if (this.searchValue) {
            partDefinitionBases = partDefinitionBases!.filter(c => {
                const s = {
                    partNumber: c,
                };

                return deepStringFind(s, this.searchValue);
            });
        }

        return partDefinitionBases;
    }

    @computed
    private get groupedPartDefinitions(): PartDefinitionGroup[] {
        if (this.partDefinitionBases.length == 0) return [];

        const groups: PartDefinitionGroup[] = [];

        for (const partDefintionBase of this.partDefinitionBases) {
            const group = new PartDefinitionGroup(partDefintionBase);
            groups.push(group);
        }

        return groups;
    }

    @computed
    private get hasMore() {
        return this.scrollCount < this.groupedPartDefinitions.length;
    }

    @bind
    @action
    private loadMore() {
        this.scrollCount += 40;
    }

    @observable
    private scrollCount = 40;
    @observable
    private searchValue: string = '';
    @observable
    private loading = false;

    @inject
    private readonly partDefinitionsStore!: PartDefinitionsStore;
    @inject
    private readonly appStore!: AppStore;
}
